import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
  MenuItem,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { PERMISSIONS } from '../protectedRoute';

const PERMISSION_LABELS = {
  [PERMISSIONS.VIEW_CALENDAR]: 'Ver Calendario',
  [PERMISSIONS.EDIT_CALENDAR]: 'Editar Calendario',
  [PERMISSIONS.VIEW_CATEGORIES]: 'Ver Categorías',
  [PERMISSIONS.EDIT_CATEGORIES]: 'Editar Categorías',
  [PERMISSIONS.VIEW_PAYMENTS]: 'Ver Pagos',
  [PERMISSIONS.EDIT_PAYMENTS]: 'Editar Pagos',
  [PERMISSIONS.VIEW_MEMBERS]: 'Ver Miembros',
  [PERMISSIONS.EDIT_MEMBERS]: 'Editar Miembros',
  [PERMISSIONS.VIEW_EMPLOYEES]: 'Ver Empleados',
  [PERMISSIONS.EDIT_EMPLOYEES]: 'Editar Empleados',
  [PERMISSIONS.VIEW_EVENTS]: 'Ver Eventos',
  [PERMISSIONS.EDIT_EVENTS]: 'Editar Eventos',
  [PERMISSIONS.VIEW_CONFIG]: 'Ver Configuración',
  [PERMISSIONS.EDIT_CONFIG]: 'Editar Configuración',
  [PERMISSIONS.VIEW_INSTRUCTOR_PAY]: 'Ver Pago a instructores',
  [PERMISSIONS.VIEW_CSCC_VENDORS]: 'Ver Vendedores CSCC',
  [PERMISSIONS.VIEW_RAFFLES]: 'Ver Sorteos',
};

const defaultPermissions = Object.fromEntries(
  Object.values(PERMISSIONS).map((k) => [k, false])
);

export default function NewEmployeePage() {
  const navigate = useNavigate();
  const navLocation = useLocation();
  const employeeId = new URLSearchParams(navLocation.search).get('id');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('employee');
  const [active, setActive] = useState(true);
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Roles múltiples (etiquetas de función, ej: "Instructor") — catálogo en employee_roles
  const [roles, setRoles] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  // Número de autorización del instructor (sale en certificados)
  const [authorizationNumber, setAuthorizationNumber] = useState('');
  const isInstructor = roles.some((rl) => /instructor/i.test(rl));

  useEffect(() => {
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, 'employee_roles'));
        setRoleOptions(snap.docs.map((d) => d.data().name).filter(Boolean).sort());
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (!employeeId) return;
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'employees', employeeId));
        if (snap.exists()) {
          const d = snap.data();
          setName(d.name || '');
          setEmail(d.email || '');
          setUsername(d.username || '');
          setRole(d.role || 'employee');
          setActive(d.active !== false);
          setPermissions({ ...defaultPermissions, ...(d.permissions || {}) });
          setRoles(Array.isArray(d.roles) ? d.roles : []);
          setAuthorizationNumber(d.authorizationNumber || '');
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [employeeId]);

  const togglePermission = (key) => {
    setPermissions({ ...permissions, [key]: !permissions[key] });
  };

  const handleSave = async () => {
    if (!email.trim() || !name.trim()) {
      setMessage({ type: 'error', text: 'Nombre y email son requeridos.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const db = getFirestore();
      const cleanRoles = [...new Set(roles.map((x) => x.trim()).filter(Boolean))];
      const data = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        username: username.trim(),
        role,
        roles: cleanRoles,
        authorizationNumber: authorizationNumber.trim(),
        active,
        permissions: role === 'admin' ? defaultPermissions : permissions,
        updatedAt: serverTimestamp(),
      };
      // Solo guardamos password si se proporciona (en producción esto debería estar hasheado en backend)
      if (password.trim()) data.password = password.trim();

      if (employeeId) {
        await updateDoc(doc(db, 'employees', employeeId), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'employees'), data);
      }

      // Roles nuevos → añadirlos al catálogo employee_roles
      const newRoles = cleanRoles.filter((x) => !roleOptions.some((o) => o.toLowerCase() === x.toLowerCase()));
      await Promise.all(newRoles.map((x) => addDoc(collection(db, 'employee_roles'), {
        name: x,
        createdAt: serverTimestamp(),
      })));

      setMessage({ type: 'success', text: 'Empleado guardado.' });
      setTimeout(() => navigate('/dashboard/employees'), 800);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Error guardando el empleado.' });
    }
    setSaving(false);
  };

  return (
    <>
      <Helmet>
        <title>{employeeId ? 'Editar Empleado' : 'Nuevo Empleado'} | BBTC</title>
      </Helmet>

      <Container maxWidth="md">
        <Typography variant="h4" sx={{ mb: 4 }}>
          {employeeId ? 'Editar Empleado' : 'Nuevo Empleado'}
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }}>{message.text}</Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <TextField
                label="Nombre completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Usuario (username)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                helperText="Identificador único para login"
              />
              <TextField
                label={employeeId ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Rol"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                fullWidth
              >
                <MenuItem value="employee">Empleado</MenuItem>
                <MenuItem value="admin">Admin (acceso total)</MenuItem>
              </TextField>
              <Autocomplete
                multiple
                freeSolo
                openOnFocus
                forcePopupIcon
                options={roleOptions}
                value={roles}
                onChange={(e, v) => setRoles(v)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Roles (funciones)"
                    placeholder="Escribe y presiona Enter para crear, ej: Instructor"
                    helperText='Los empleados con rol "Instructor" son los que salen en Cursos y en la hoja de asistencia impresa.'
                  />
                )}
              />
              {isInstructor && (
                <TextField
                  label="Número de autorización (instructor)"
                  value={authorizationNumber}
                  onChange={(e) => setAuthorizationNumber(e.target.value)}
                  fullWidth
                  placeholder="Ej: 222118"
                  helperText="Requerido para instructores — sale en los certificados de cursos."
                />
              )}
              <FormControlLabel
                control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
                label="Cuenta activa"
              />

              <Divider />

              <Typography variant="h6">Permisos</Typography>
              {role === 'admin' ? (
                <Alert severity="info">
                  Los usuarios con rol Admin tienen acceso completo a todas las áreas del sistema.
                </Alert>
              ) : (
                <FormGroup>
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={permissions[key] || false}
                          onChange={() => togglePermission(key)}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              )}

              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/dashboard/employees')}>
                  Cancelar
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
